import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast, showEmailVerificationToast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import SearchableSelect from '@/components/SearchableSelect';
import { Calendar, Users, Bell, Sparkles, Check, Clock, Shield, Eye, EyeOff, Fingerprint } from 'lucide-react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

import { COUNTRIES, NATIONALITIES } from '@/constants';

// Helper to format relative time
const formatLastLogin = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const AuthDialog = ({ open, onClose, customMessage, defaultToSignup = false, inviteToken = null }) => {
  const { login } = useAuth();
  const authNavigate = useNavigate();
  const isIOSApp = useIsIOSApp();
  const [isLogin, setIsLogin] = useState(!defaultToSignup);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [deviceSupportsBiometric, setDeviceSupportsBiometric] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    nationality: '',
    birth_country: '',
    birth_city: '',
    residence_country: '',
    residence_city: '',
    about_me: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // When dialog opens with customMessage, default to signup
  useEffect(() => {
    if (open && customMessage) {
      setIsLogin(false);
    }
  }, [open, customMessage]);

  // Check device biometric support on mount
  useEffect(() => {
    const checkDeviceSupport = async () => {
      if (!window.PublicKeyCredential) return;
      try {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          setDeviceSupportsBiometric(true);
        } else {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setDeviceSupportsBiometric(available);
        }
      } catch {
        setDeviceSupportsBiometric(false);
      }
    };
    checkDeviceSupport();
  }, []);

  // Show welcome back toast with activity summary
  const showWelcomeBackToast = (userName, welcomeData) => {
    const { activity, last_login, is_returning_user } = welcomeData;
    
    if (!is_returning_user) {
      toast.success(`Welcome back, ${userName}!`, {
        description: "Great to see you again!",
        duration: 4000,
      });
      return;
    }

    // Build activity summary
    const activityItems = [];
    if (activity.upcoming_events > 0) {
      activityItems.push(`${activity.upcoming_events} upcoming event${activity.upcoming_events > 1 ? 's' : ''} this week`);
    }
    if (activity.pending_requests > 0) {
      activityItems.push(`${activity.pending_requests} new connection request${activity.pending_requests > 1 ? 's' : ''}`);
    }
    if (activity.connections > 0 && activityItems.length < 2) {
      activityItems.push(`${activity.connections} connection${activity.connections > 1 ? 's' : ''} in your network`);
    }

    const lastLoginText = last_login ? `Last seen ${formatLastLogin(last_login)}` : '';
    
    // Custom rich toast for returning users
    toast.custom((t) => (
      <div className="bg-white rounded-xl shadow-xl border-2 border-accent/20 p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-foreground">Welcome back, {userName}!</h4>
            {lastLoginText && (
              <p className="text-xs text-muted-foreground mt-0.5">{lastLoginText}</p>
            )}
            {activityItems.length > 0 && (
              <div className="mt-2 space-y-1">
                {activityItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                    {item.includes('event') && <Calendar className="w-3 h-3 text-accent" />}
                    {item.includes('connection') && <Users className="w-3 h-3 text-primary" />}
                    {item.includes('request') && <Bell className="w-3 h-3 text-accent" />}
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    ), { duration: 5000 });
  };

  // Handle biometric login — uses stored email from localStorage for credential lookup
  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      // Check for stored email from last successful login/enrollment
      const storedEmail = localStorage.getItem('biometric_email');
      
      // Build the options URL — with email if available (works with old credentials),
      // without email for discoverable credential (passkey) flow
      const optionsUrl = storedEmail
        ? `${API}/webauthn/login/options?email=${encodeURIComponent(storedEmail)}`
        : `${API}/webauthn/login/options`;

      const { data } = await axios.get(optionsUrl);
      const options = typeof data.options === 'string' ? JSON.parse(data.options) : data.options;
      const sessionId = data.session_id;

      // Trigger biometric prompt
      const credential = await startAuthentication({ optionsJSON: options });

      // Verify with server
      const { data: loginResult } = await axios.post(`${API}/webauthn/login/verify?session_id=${encodeURIComponent(sessionId)}`, credential);
      
      // Store email for future biometric logins
      if (loginResult.user?.email) {
        localStorage.setItem('biometric_email', loginResult.user.email);
      }
      
      login(loginResult.token, loginResult.user);
      toast.success('Welcome back!');
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Biometric login failed';
      if (msg.includes('not found') || msg.includes('No biometric') || msg.includes('not recognized')) {
        toast.error('No biometric credentials found for this device.', {
          description: 'Please log in with your password, then enable biometric login in Profile > Security.',
          duration: 6000,
        });
      } else if (err.name === 'NotAllowedError' || msg.includes('operation either timed out') || msg.includes('cancelled')) {
        toast.info('Biometric login cancelled');
      } else if (msg.includes('No passkeys') || msg.includes('no credentials')) {
        toast.error('No biometric credentials found for this device.', {
          description: 'Please log in with your password, then enable biometric login in Profile > Security.',
          duration: 6000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields for signup - only name, email, password are required
    // Other fields (nationality, location) are optional per Apple App Store guidelines
    if (!isLogin) {
      const errors = {};
      const requiredFields = {
        name: 'Name',
        email: 'Email',
        password: 'Password'
      };
      
      Object.entries(requiredFields).forEach(([field, label]) => {
        if (!formData[field] || formData[field].trim() === '') {
          errors[field] = `${label} is required`;
        }
      });
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        const firstError = Object.values(errors)[0];
        toast.error('Please fill in all required fields', {
          description: firstError
        });
        setLoading(false);
        return;
      }

      if (!agreedToTerms) {
        toast.error('Please agree to the Terms of Service and Community Guidelines');
        return;
      }
    }
    
    // Clear validation errors
    setValidationErrors({});
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const data = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, ...(inviteToken ? { invite_token: inviteToken } : {}) };

      const response = await axios.post(`${API}${endpoint}`, data);
      
      // Check if 2FA is required (admin login)
      if (response.data.requires_2fa) {
        setTwoFactorData({
          email: formData.email,
          tempToken: response.data.temp_token
        });
        setShowTwoFactor(true);
        toast.info('Verification code sent!', {
          description: 'Check your email for the 6-digit code.',
        });
        setLoading(false);
        return;
      }
      
      login(response.data.token, response.data.user);
      
      // Store email for future biometric logins
      if (response.data.user?.email) {
        localStorage.setItem('biometric_email', response.data.user.email);
      }
      
      if (isLogin && response.data.welcome_data) {
        // Show personalized welcome back toast for login
        showWelcomeBackToast(response.data.user.name.split(' ')[0], response.data.welcome_data);
      } else if (!isLogin) {
        // Show ONLY the email verification toast for signup (with red border)
        // Don't show "Account created" - that will show after email is verified
        showEmailVerificationToast(formData.email);
      }
      
      // Longer delay to ensure toast renders and is visible before dialog closes
      setTimeout(() => {
        onClose();
        // Redirect new users (empty profile) to onboarding after login
        if (isLogin) {
          const u = response.data.user;
          const needsOnboarding = !u.residence_country && (!u.groups || u.groups.length === 0) && (!u.interests || u.interests.length === 0);
          if (needsOnboarding) {
            authNavigate('/onboarding');
          }
        }
      }, 500);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      const status = error.response?.status;
      
      if (isLogin && (detail.includes('Invalid') || detail.includes('incorrect') || detail.includes('credentials') || detail.includes('not found'))) {
        toast.error('Login failed', {
          description: 'Incorrect email or password. If you signed up on the website, try resetting your password.',
          duration: 8000,
          action: {
            label: 'Reset Password',
            onClick: () => {
              setForgotPasswordEmail(formData.email);
              setShowForgotPassword(true);
            }
          }
        });
      } else if (!isLogin && detail.includes('already registered')) {
        toast.error('This email is already registered', {
          description: 'Try logging in instead, or use a different email.',
          duration: 6000,
        });
      } else if (!isLogin && status === 500) {
        toast.error('Sign up failed', {
          description: 'Something went wrong. Please check all fields and try again.',
          duration: 6000,
        });
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error(isLogin ? 'Login failed. Please try again.' : 'Sign up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        email: twoFactorData.email,
        code: twoFactorCode,
        temp_token: twoFactorData.tempToken
      });
      
      login(response.data.token, response.data.user);
      
      if (response.data.welcome_data) {
        showWelcomeBackToast(response.data.user.name.split(' ')[0], response.data.welcome_data);
      }
      
      toast.success('Admin login successful!', {
        description: 'Two-factor authentication verified.',
      });
      
      setShowTwoFactor(false);
      setTwoFactorCode('');
      setTwoFactorData(null);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear validation error for this field when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Helper to get input class with error styling
  const getInputClass = (fieldName) => {
    const hasError = validationErrors[fieldName];
    if (hasError) {
      return "h-12 rounded-xl border-2 border-red-500 focus:border-red-500";
    }
    return "h-12 rounded-xl border-2";
  };

  // Helper to get select trigger class with error styling
  const getSelectClass = (fieldName) => {
    const hasError = validationErrors[fieldName];
    if (hasError) {
      return "h-12 rounded-xl border-2 border-red-500";
    }
    return "h-12 rounded-xl border-2";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden pb-[max(1.5rem,env(safe-area-inset-bottom))] rounded-none sm:rounded-lg" data-testid="auth-dialog" aria-describedby="auth-dialog-description">
        {/* 2FA Verification Screen */}
        {showTwoFactor ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">
                Admin Verification
              </DialogTitle>
              <p id="auth-dialog-description" className="sr-only">
                Enter the verification code sent to your email
              </p>
            </DialogHeader>
            <form onSubmit={handleVerify2FA} className="space-y-6 py-4">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  A 6-digit verification code was sent to<br/>
                  <span className="font-semibold text-foreground">{twoFactorData?.email}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="2fa-code">Verification Code</Label>
                <Input
                  id="2fa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl border-2"
                  autoFocus
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl font-bold"
                disabled={loading || twoFactorCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowTwoFactor(false);
                  setTwoFactorCode('');
                  setTwoFactorData(null);
                }}
              >
                ← Back to Login
              </Button>
            </form>
          </>
        ) : (
        <>
        <DialogHeader>
          {customMessage && (
            <div className="bg-gradient-to-r from-primary to-accent text-white px-4 py-3 rounded-xl mb-4 text-center font-semibold" data-testid="auth-custom-message">
              {customMessage}
            </div>
          )}
          <DialogTitle className="text-2xl font-heading font-bold">
            {isLogin ? 'Welcome Back!' : (isIOSApp ? 'Join ExpatsSphere' : 'Join ExpatsSphere FREE')}
          </DialogTitle>
          <p id="auth-dialog-description" className="sr-only">
            {isLogin ? 'Login to your account' : 'Create a new account to join ExpatsSphere'}
          </p>
          
          {/* Membership Benefits - Show on Signup */}
          {!isLogin && (
            <div className="mt-3 p-4 bg-accent/10 rounded-xl border border-accent/20">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold text-primary">{isIOSApp ? 'Free to join!' : 'FREE for 1 year!'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>Unlimited events</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>Join any group</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>Direct messaging</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />
                  <span>Push notifications</span>
                </div>
              </div>
            </div>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!isLogin && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={getInputClass('name')}
                  data-testid="signup-name-input"
                />
                {validationErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nationality">Nationality <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                <SearchableSelect
                  options={NATIONALITIES}
                  value={formData.nationality}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, nationality: value }));
                    if (validationErrors.nationality) {
                      setValidationErrors(prev => ({ ...prev, nationality: null }));
                    }
                  }}
                  placeholder="Select your nationality"
                  searchPlaceholder="Type to search..."
                  data-testid="signup-nationality-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birth_city">City of Birth <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                  <Input
                    id="birth_city"
                    name="birth_city"
                    value={formData.birth_city}
                    onChange={handleChange}
                    className={getInputClass('birth_city')}
                    data-testid="signup-birth-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birth_country">Country of Birth <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                  <SearchableSelect
                    options={COUNTRIES}
                    value={formData.birth_country}
                    onValueChange={(value) => {
                      setFormData(prev => ({ ...prev, birth_country: value }));
                      if (validationErrors.birth_country) {
                        setValidationErrors(prev => ({ ...prev, birth_country: null }));
                      }
                    }}
                    placeholder="Select country"
                    searchPlaceholder="Type to search..."
                    data-testid="signup-birth-country-input"
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={getInputClass('email')}
              data-testid="auth-email-input"
            />
            {validationErrors.email && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                className={`${getInputClass('password')} pr-10`}
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {validationErrors.password && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
            )}
            {isLogin && (
              <div className="flex items-center justify-between mt-1">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordEmail(formData.email);
                    setShowForgotPassword(true);
                  }}
                  className="text-sm text-primary hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </div>

          {!isLogin && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="residence_city">Current City <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                  <Input
                    id="residence_city"
                    name="residence_city"
                    value={formData.residence_city}
                    onChange={handleChange}
                    className={getInputClass('residence_city')}
                    data-testid="signup-residence-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="residence_country">Current Country <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                  <SearchableSelect
                    options={COUNTRIES}
                    value={formData.residence_country}
                    onValueChange={(value) => {
                      setFormData(prev => ({ ...prev, residence_country: value }));
                      if (validationErrors.residence_country) {
                        setValidationErrors(prev => ({ ...prev, residence_country: null }));
                      }
                    }}
                    placeholder="Select country"
                    searchPlaceholder="Type to search..."
                    data-testid="signup-residence-country-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="about_me">About Me (Optional)</Label>
                <Textarea
                  id="about_me"
                  name="about_me"
                  value={formData.about_me}
                  onChange={handleChange}
                  className="rounded-xl border-2 min-h-[80px]"
                  placeholder="Tell us about yourself..."
                  data-testid="signup-about-me-input"
                />
              </div>
            </>
          )}

          {/* Terms of Service checkbox (signup only) */}
          {!isLogin && (
            <div className="flex items-start gap-2 mt-2">
              <input
                type="checkbox"
                id="terms-agree"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 accent-primary"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms-agree" className="text-xs text-muted-foreground leading-tight">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-primary underline font-medium">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" className="text-primary underline font-medium">Privacy Policy</a>.
                I understand that objectionable content is not allowed and my account may be suspended for violations.
              </label>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-bold brutal-shadow"
            data-testid="auth-submit-button"
          >
            {loading ? 'Please wait...' : isLogin ? 'Login' : 'Sign Up'}
          </Button>

          {/* Biometric Login Button - Show on any device that supports biometrics */}
          {isLogin && deviceSupportsBiometric && (
            <>
              <div className="relative flex items-center justify-center my-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <span className="relative bg-white px-3 text-xs text-muted-foreground">or</span>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={biometricLoading}
                onClick={handleBiometricLogin}
                className="w-full h-12 rounded-full border-2 font-bold flex items-center justify-center gap-2"
                data-testid="biometric-login-button"
              >
                <Fingerprint className="w-5 h-5" />
                {biometricLoading ? 'Verifying...' : 'Login with Biometrics'}
              </Button>
            </>
          )}

          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="auth-toggle-button"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
            </button>
          </div>
        </form>
        </>
        )}
      </DialogContent>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setForgotPasswordEmail('');
          setForgotPasswordSent(false);
        }
      }}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-center">
              {forgotPasswordSent ? 'Check Your Email' : 'Reset Password'}
            </DialogTitle>
          </DialogHeader>
          
          {forgotPasswordSent ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-muted-foreground mb-4">
                If an account exists for <strong>{forgotPasswordEmail}</strong>, 
                you'll receive a password reset link shortly.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-semibold text-amber-800 mb-2">📧 Can't find the email?</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Check your <strong>Spam</strong> or <strong>Junk</strong> folder</li>
                  <li>• Look in <strong>Promotions</strong> tab (Gmail)</li>
                  <li>• Add <strong>noreply@expatssphere.com</strong> to your contacts</li>
                </ul>
              </div>
              <Button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordSent(false);
                  setForgotPasswordEmail('');
                }}
                className="rounded-full"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              try {
                await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
                setForgotPasswordSent(true);
              } catch (error) {
                // Still show success to prevent email enumeration
                setForgotPasswordSent(true);
              } finally {
                setLoading(false);
              }
            }} className="space-y-4">
              <p className="text-muted-foreground text-center">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="h-12 rounded-xl border-2"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !forgotPasswordEmail}
                className="w-full h-12 rounded-full font-bold"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Back to Login
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AuthDialog;
